<script setup lang="ts">
    import type { DocumentQuotaState } from '~/composables/useDocumentQuota';
    import type { QuotaType } from '~/stores/documentQuotaStore';
    import { useDocumentQuota } from '~/composables/useDocumentQuota';

    const props = withDefaults(
        defineProps<{
            variant?: 'header' | 'form-sidebar' | 'nav';
            quotaType?: QuotaType;
            // Identifies which page hosts the 'header' variant (the box is reused across the
            // quotes/invoices, products and expenses list pages). Sent as the tracking `page`.
            trackingPage?: string;
        }>(),
        {
            variant: 'header',
            quotaType: 'documents',
            trackingPage: undefined,
        }
    );

    const { t } = useTranslation();
    const { hasQuota, limit, used, percentage, state } = useDocumentQuota(props.quotaType);

    // Maps the rendering variant to the tracking surface, and resolves the page label: the
    // sidebar/document-sidebar surfaces are unambiguous; the page surface uses `trackingPage`.
    const trackingSurface = computed(() => {
        if (props.variant === 'nav') return 'sidebar';
        if (props.variant === 'form-sidebar') return 'document_sidebar';
        return 'page';
    });

    const quotaCtaTracking = computed(() => ({
        eventName: 'quota_box_cta_clicked',
        properties: {
            surface: trackingSurface.value,
            page: props.variant === 'header' ? props.trackingPage ?? null : null,
            quota_type: props.quotaType,
            state: state.value,
            used: used.value,
            limit: limit.value,
        },
    }));

    const contextColor = computed(() => {
        const map: Record<DocumentQuotaState, string> = {
            below_threshold: '#00AC98',
            above_threshold: '#F97316',
            reached: '#EF4444',
        };
        return map[state.value as DocumentQuotaState];
    });

    const isBelowThreshold = computed(() => state.value === 'below_threshold');

    const boxBgColor = computed(() => (isBelowThreshold.value ? '#FFFFFF' : `${contextColor.value}2A`));

    const boxBorderColor = computed(() => (isBelowThreshold.value ? '#E5E5E5' : `${contextColor.value}1A`));

    const trackBgColor = computed(() => (isBelowThreshold.value ? '#E5E5E5' : `${contextColor.value}2A`));

    const ctaContent = computed(() => ({
        label: 'invoicing.quota.cta_change_plan',
        url: '/settings/subscription',
        type: 'dark' as const,
    }));
</script>

<template>
    <!-- VARIANT: Header — pill badge next to page title -->
    <div
        v-if="hasQuota && variant === 'header'"
        class="inline-flex items-center gap-3 rounded-xl border px-4 py-2.5"
        :style="{ backgroundColor: boxBgColor, borderColor: boxBorderColor }"
    >
        <div class="inline-flex flex-col gap-[2px]">
            <span class="text-sm font-bricolage font-normal text-neutral-900 leading-none">
                <span
                    class="font-bold"
                    :style="{ color: contextColor }"
                    >{{ used }}</span
                >/{{ limit }} documents
            </span>
            <div
                class="h-[5px] w-full rounded-full overflow-hidden"
                :style="{ backgroundColor: trackBgColor }"
            >
                <div
                    class="h-full rounded-full transition-all duration-300"
                    :style="{ backgroundColor: contextColor, width: `${Math.min(percentage, 100)}%` }"
                />
            </div>
        </div>
        <UiAction
            :content="ctaContent"
            :tracking="quotaCtaTracking"
            modifier="small"
        />
    </div>

    <!-- VARIANT: Form sidebar — top of right sidebar in document editor -->
    <div
        v-if="hasQuota && variant === 'form-sidebar'"
        class="flex flex-col gap-2 p-4 rounded-xl border"
        :style="{ backgroundColor: boxBgColor, borderColor: boxBorderColor }"
    >
        <div class="flex items-center justify-between text-bricolage">
            <span class="text-sm font-semibold text-neutral-900">{{ t('invoicing.quota.limit_label') }}</span>
            <div class="text-sm text-neutral-900">
                <span
                    class="font-bold"
                    :style="{ color: contextColor }"
                    >{{ used }}</span
                >
                <span>/{{ limit }} documents</span>
            </div>
        </div>
        <div
            class="h-[5px] w-full rounded-full bg-neutral-200 overflow-hidden"
            :style="{ backgroundColor: trackBgColor }"
        >
            <div
                class="h-full rounded-full transition-all duration-300"
                :style="{ backgroundColor: contextColor, width: `${Math.min(percentage, 100)}%` }"
            />
        </div>
        <UiAction
            :content="ctaContent"
            :tracking="quotaCtaTracking"
            modifier="fullMedium"
        />
    </div>

    <!-- VARIANT: Nav — bottom of main sidebar navigation -->
    <div
        v-if="hasQuota && variant === 'nav'"
        class="mx-2 mb-2 flex flex-col gap-1.5 rounded-lg bg-white/10 p-3"
    >
        <div class="flex items-center justify-between">
            <span class="text-[10px] text-white font-bricolage font-semibold">{{ t('invoicing.quota.limit_label') }}</span>
            <span class="text-[10px] text-white"
                ><span
                    class="font-bold"
                    :style="{ color: contextColor }"
                    >{{ used }}</span
                >/{{ limit }} documents</span
            >
        </div>
        <div class="h-[5px] w-full rounded-full bg-black/40 overflow-hidden mb-1">
            <div
                class="h-full rounded-full transition-all duration-300"
                :style="{ backgroundColor: contextColor, width: `${Math.min(percentage, 100)}%` }"
            />
        </div>
        <UiAction
            :content="{ label: 'invoicing.quota.cta_change_plan', url: '/settings/subscription', type: 'lightDark' }"
            :tracking="quotaCtaTracking"
            modifier="fullMedium"
        />
    </div>
</template>
